/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Universal Shared Types & Schemas for CMS & Content Management (Phase 3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { z } from '@hono/zod-openapi';

// ============================================================================
// 1. CMS CATEGORIES
// ============================================================================

export const CmsCategorySchema = z
  .object({
    id: z.string().openapi({ description: 'Category unique identifier', example: 'cat-news-01' }),
    name: z.string().openapi({ description: 'Category display name', example: 'Product Updates' }),
    slug: z.string().openapi({ description: 'URL slug', example: 'product-updates' }),
    description: z.string().nullable().optional().openapi({ description: 'Category description' }),
    sortOrder: z.number().default(0).openapi({ description: 'Sort sequence', example: 1 }),
    isActive: z.boolean().default(true).openapi({ description: 'Is category active', example: true }),
  })
  .openapi('CmsCategory');

export type CmsCategory = z.infer<typeof CmsCategorySchema>;

export const CreateCmsCategorySchema = z
  .object({
    name: z.string().min(1, 'Category name is required').openapi({ example: 'Product Updates' }),
    slug: z.string().optional().openapi({ example: 'product-updates' }),
    description: z.string().optional().openapi({ example: 'Latest news and updates' }),
    sortOrder: z.number().default(0).optional().openapi({ example: 0 }),
    isActive: z.boolean().default(true).optional().openapi({ example: true }),
  })
  .openapi('CreateCmsCategory');

export type CreateCmsCategoryInput = z.infer<typeof CreateCmsCategorySchema>;

export const UpdateCmsCategorySchema = CreateCmsCategorySchema.partial().openapi('UpdateCmsCategory');
export type UpdateCmsCategoryInput = z.infer<typeof UpdateCmsCategorySchema>;

// ============================================================================
// 2. CMS BLOG POSTS
// ============================================================================

export const CmsPostSchema = z
  .object({
    id: z.string().openapi({ description: 'Post unique identifier', example: 'post-launch-v2' }),
    authorId: z.string().openapi({ description: 'Author user ID' }),
    authorName: z.string().optional().openapi({ description: 'Author display name', example: 'Le Viet Quang' }),
    categoryId: z.string().nullable().optional().openapi({ description: 'Category identifier' }),
    categoryName: z.string().optional().openapi({ description: 'Category display name', example: 'Product Updates' }),
    title: z.string().openapi({ description: 'Post title', example: 'Announcing Sophia Platform 2.0' }),
    slug: z.string().openapi({ description: 'Unique post URL slug', example: 'announcing-sophia-platform-v2' }),
    summary: z.string().nullable().optional().openapi({ description: 'Short summary for SEO cards' }),
    content: z.string().openapi({ description: 'Full Markdown / HTML post body content' }),
    coverImageUrl: z.string().nullable().optional().openapi({ description: 'Featured banner image URL' }),
    locale: z.string().default('en').openapi({ description: 'Language code (e.g., en, vi)', example: 'en' }),
    isPublished: z.boolean().default(false).openapi({ description: 'Publication status', example: true }),
    publishedAt: z.number().nullable().optional().openapi({ description: 'Unix timestamp when published' }),
    createdAt: z.number().openapi({ description: 'Unix timestamp created' }),
    updatedAt: z.number().openapi({ description: 'Unix timestamp last updated' }),
  })
  .openapi('CmsPost');

export type CmsPost = z.infer<typeof CmsPostSchema>;

export const CreateCmsPostSchema = z
  .object({
    categoryId: z.string().nullable().optional().openapi({ example: 'cat-news-01' }),
    title: z.string().min(1, 'Title is required').openapi({ example: 'Introducing Edge AI Routing' }),
    slug: z.string().optional().openapi({ example: 'introducing-edge-ai-routing' }),
    summary: z.string().optional().openapi({ example: 'Explore how edge AI routing improves latency.' }),
    content: z.string().min(1, 'Content body is required').openapi({ example: '# Introduction\n\nContent details...' }),
    coverImageUrl: z.string().optional().openapi({ example: 'https://assets.sophiaplatform.com/blog/cover.jpg' }),
    locale: z.string().default('en').optional().openapi({ example: 'en' }),
    isPublished: z.boolean().default(false).optional().openapi({ example: true }),
  })
  .openapi('CreateCmsPost');

export type CreateCmsPostInput = z.infer<typeof CreateCmsPostSchema>;

export const UpdateCmsPostSchema = CreateCmsPostSchema.partial().openapi('UpdateCmsPost');
export type UpdateCmsPostInput = z.infer<typeof UpdateCmsPostSchema>;

export const CmsPostListResponseSchema = z
  .object({
    items: z.array(CmsPostSchema),
    total: z.number().openapi({ example: 42 }),
    page: z.number().openapi({ example: 1 }),
    limit: z.number().openapi({ example: 10 }),
    totalPages: z.number().openapi({ example: 5 }),
  })
  .openapi('CmsPostListResponse');

export type CmsPostListResponse = z.infer<typeof CmsPostListResponseSchema>;

// ============================================================================
// 3. CUSTOM PAGES
// ============================================================================

export const CmsPageSchema = z
  .object({
    id: z.string().openapi({ description: 'Page unique identifier', example: 'page-about' }),
    title: z.string().openapi({ description: 'Page title', example: 'About Us' }),
    slug: z.string().openapi({ description: 'Page URL slug', example: 'about' }),
    content: z.string().openapi({ description: 'Page body in Markdown or HTML' }),
    locale: z.string().default('en').openapi({ description: 'Page locale', example: 'en' }),
    isPublished: z.boolean().default(true).openapi({ description: 'Visibility state', example: true }),
    metaTitle: z.string().nullable().optional().openapi({ description: 'SEO Meta Title' }),
    metaDescription: z.string().nullable().optional().openapi({ description: 'SEO Meta Description' }),
    sortOrder: z.number().default(0).openapi({ description: 'Sort sequence', example: 1 }),
    createdAt: z.number().openapi({ description: 'Unix timestamp created' }),
    updatedAt: z.number().openapi({ description: 'Unix timestamp updated' }),
  })
  .openapi('CmsPage');

export type CmsPage = z.infer<typeof CmsPageSchema>;

export const CreateCmsPageSchema = z
  .object({
    title: z.string().min(1, 'Page title is required').openapi({ example: 'Privacy Policy' }),
    slug: z.string().min(1, 'Slug is required').openapi({ example: 'privacy' }),
    content: z.string().min(1, 'Page content is required').openapi({ example: '# Privacy Policy\n\nYour privacy is paramount...' }),
    locale: z.string().default('en').optional().openapi({ example: 'en' }),
    isPublished: z.boolean().default(true).optional().openapi({ example: true }),
    metaTitle: z.string().optional().openapi({ example: 'Privacy Policy - Sophia Platform' }),
    metaDescription: z.string().optional().openapi({ example: 'Our privacy and data compliance guidelines.' }),
    sortOrder: z.number().default(0).optional().openapi({ example: 0 }),
  })
  .openapi('CreateCmsPage');

export type CreateCmsPageInput = z.infer<typeof CreateCmsPageSchema>;

export const UpdateCmsPageSchema = CreateCmsPageSchema.partial().openapi('UpdateCmsPage');
export type UpdateCmsPageInput = z.infer<typeof UpdateCmsPageSchema>;

export const CmsPageListResponseSchema = z
  .object({
    items: z.array(CmsPageSchema),
    total: z.number().openapi({ example: 4 }),
    page: z.number().openapi({ example: 1 }),
    limit: z.number().openapi({ example: 20 }),
    totalPages: z.number().openapi({ example: 1 }),
  })
  .openapi('CmsPageListResponse');

export type CmsPageListResponse = z.infer<typeof CmsPageListResponseSchema>;

// ============================================================================
// 4. NAVIGATION MENUS
// ============================================================================

export type NavigationItem = {
  id: string;
  label: string;
  url: string;
  href?: string;
  target?: '_self' | '_blank';
  isExternal?: boolean;
  sortOrder?: number;
  icon?: string;
  badge?: string;
  description?: string;
  labels?: Record<string, string>;
  children?: NavigationItem[];
};

export const NavigationItemSchema: z.ZodType<NavigationItem, z.ZodTypeDef, any> = z.lazy(() =>
  z.object({
    id: z.string().openapi({ description: 'Item unique identifier', example: 'nav-pricing' }),
    label: z.string().openapi({ description: 'Navigation menu title/label', example: 'Pricing' }),
    url: z.string().default('').openapi({ description: 'Destination link or relative path', example: '/pricing' }),
    href: z.string().optional().openapi({ description: 'Alias for destination link', example: '/pricing' }),
    target: z.enum(['_self', '_blank']).default('_self').openapi({ example: '_self' }),
    isExternal: z.boolean().default(false).openapi({ example: false }),
    sortOrder: z.number().default(0).openapi({ example: 1 }),
    icon: z.string().optional().openapi({ description: 'Lucide icon or identifier', example: 'Sparkles' }),
    badge: z.string().optional().openapi({ description: 'Highlight badge text', example: 'New' }),
    description: z.string().optional().openapi({ description: 'Short subtitle or tooltip description', example: 'AI Content Generation' }),
    labels: z.record(z.string()).optional().openapi({ description: 'Multilingual label dictionary' }),
    children: z.array(NavigationItemSchema).optional().openapi({ description: 'Submenu children items' }),
  })
).openapi('NavigationItem', {
  type: 'object',
  description: 'Navigation Item schema',
});

export const NavigationMenuConfigSchema = z
  .object({
    header: z.array(NavigationItemSchema).default([]).openapi({ description: 'Header navigation items' }),
    footer: z.array(NavigationItemSchema).default([]).openapi({ description: 'Footer navigation items' }),
    updatedAt: z.number().optional().openapi({ description: 'Timestamp of last modification' }),
  })
  .openapi('NavigationMenuConfig');

export type NavigationMenuConfig = z.infer<typeof NavigationMenuConfigSchema>;

export const NavigationMenuSchema = NavigationMenuConfigSchema;
export type NavigationMenu = NavigationMenuConfig;

export const UpdateNavigationMenuSchema = NavigationMenuConfigSchema.openapi('UpdateNavigationMenu');
export type UpdateNavigationMenuInput = z.infer<typeof UpdateNavigationMenuSchema>;

